import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Calculator, BookOpen, Mail, Play, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const suggestions = [
  "Comment estimer mon budget ?",
  "Comment choisir mon terrain ?",
  "Quelles sont les étapes du permis ?",
];

const kindIcon = { tool: Calculator, article: BookOpen, lead: Mail, video: Play };

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis l'assistant de construiremaison.net. Posez-moi vos questions sur la construction de votre maison : budget, terrain, démarches... Je vous orienterai vers le bon simulateur ou le bon guide.", suggestions: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(crypto.randomUUID());
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const goTo = (s) => {
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event: "suggestion_click", label: s.label, kind: s.kind, href: s.href }),
    }).catch(() => {});
    const href = s.href;
    if (href.startsWith("#")) {
      setOpen(false);
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 250);
    } else if (href.startsWith("/")) {
      setOpen(false);
      window.location.assign(href);
    } else {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "", suggestions: [] }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, session_id: sessionId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
          if (payload.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: payload.error, suggestions: [] };
              return copy;
            });
          }
          if (payload.suggestions) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], suggestions: payload.suggestions };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connexion impossible. Réessayez dans un instant.", suggestions: [] };
        return copy;
      });
    }
    setLoading(false);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-white border border-border rounded-xl shadow-deep flex flex-col overflow-hidden"
            data-testid="ai-chat-window"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-bone">
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-terra text-white">
                  <Sparkles className="w-4 h-4" />
                </span>
                <div>
                  <p className="font-sub font-bold text-sm leading-tight">Assistant construction</p>
                  <p className="text-[11px] text-stone2 leading-tight">Propulsé par IA — GPT 5.4 Mini</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Fermer le chat" data-testid="ai-chat-close" className="text-ink/50 hover:text-ink transition-colors duration-200">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i}>
                  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === "user" ? "bg-terra text-white" : "bg-sand/70 text-ink"
                      }`}
                    >
                      {m.content || (loading && i === messages.length - 1 ? "..." : "")}
                    </div>
                  </div>
                  {m.role === "assistant" && m.suggestions && m.suggestions.length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid={`ai-chat-actions-${i}`}>
                      <p className="font-sub text-[10px] uppercase tracking-[0.15em] text-stone2 px-1">Pour aller plus loin</p>
                      {m.suggestions.map((s, j) => {
                        const Icon = kindIcon[s.kind] || ArrowUpRight;
                        return (
                          <button
                            key={j}
                            onClick={() => goTo(s)}
                            data-testid={`ai-chat-action-${i}-${j}`}
                            className={`w-full flex items-center gap-2.5 text-left text-xs font-sub font-medium rounded-lg border px-3 py-2.5 transition-[background-color,color,border-color] duration-200 ${
                              s.kind === "lead"
                                ? "bg-terra text-white border-terra hover:bg-terra-dark"
                                : "border-terra/30 text-terra hover:bg-terra hover:text-white"
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`ai-chat-suggestion-${suggestions.indexOf(s)}`}
                      className="text-xs font-sub border border-terra/40 text-terra rounded-full px-3.5 py-1.5 hover:bg-terra hover:text-white transition-[background-color,color] duration-200"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-2 px-4 py-3 border-t border-border"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Votre question..."
                data-testid="ai-chat-input"
                className="flex-1 bg-bone border border-border rounded-full px-4 py-2.5 text-sm outline-none focus:border-terra transition-colors duration-200"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                aria-label="Envoyer"
                data-testid="ai-chat-send-button"
                className="flex items-center justify-center w-10 h-10 rounded-full bg-terra text-white hover:bg-terra-dark transition-colors duration-200 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 1.6, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Ouvrir l'assistant IA"
        data-testid="ai-chat-bubble"
        className="fixed bottom-5 right-4 sm:right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-terra text-white shadow-deep hover:bg-terra-dark hover:scale-105 transition-[background-color,transform] duration-200"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </motion.button>
    </>
  );
}
